/**
 * Componente Progress
 *
 * Barra de progreso y anillo de progreso.
 * Proporciona feedback visual del avance.
 */

import { cn } from '@/core/utils';
import type { Layer, Size } from '@/core/types';
import { getLayerColors } from '@/core/constants';

interface ProgressBarProps {
  value: number; // 0-100
  max?: number;
  layer?: Layer;
  size?: Size;
  showLabel?: boolean;
  animated?: boolean;
  className?: string;
}

const sizeStyles: Record<Size, string> = {
  xs: 'h-1',
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-3',
  xl: 'h-4',
};

export function ProgressBar({
  value,
  max = 100,
  layer = 'maintenance',
  size = 'md',
  showLabel = false,
  animated = true,
  className,
}: ProgressBarProps) {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const colors = getLayerColors(layer);

  return (
    <div className={cn('w-full', className)}>
      {showLabel && (
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-slate-400">Progreso</span>
          <span className={cn('text-xs font-medium', colors.text)}>
            {Math.round(percentage)}%
          </span>
        </div>
      )}

      <div
        className={cn(
          'w-full bg-slate-800 rounded-full overflow-hidden',
          sizeStyles[size]
        )}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
      >
        <div
          className={cn(
            'h-full rounded-full',
            colors.bg,
            animated && 'transition-all duration-500 ease-out'
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Anillo de progreso circular
 */
interface ProgressRingProps {
  value: number; // 0-100
  size?: number; // px
  strokeWidth?: number;
  layer?: Layer;
  showValue?: boolean;
  className?: string;
}

export function ProgressRing({
  value,
  size = 48,
  strokeWidth = 4,
  layer = 'maintenance',
  showValue = true,
  className,
}: ProgressRingProps) {
  const percentage = Math.min(Math.max(value, 0), 100);
  const colors = getLayerColors(layer);

  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-slate-800"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={colors.accent}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-500 ease-out"
        />
      </svg>

      {showValue && (
        <span
          className={cn(
            'absolute text-xs font-semibold',
            colors.text
          )}
        >
          {Math.round(percentage)}%
        </span>
      )}
    </div>
  );
}

/**
 * Indicador de progreso por capas
 */
interface LayerProgressIndicatorProps {
  survival: number;
  maintenance: number;
  growth: number;
  size?: Size;
  className?: string;
}

export function LayerProgressIndicator({
  survival,
  maintenance,
  growth,
  size = 'md',
  className,
}: LayerProgressIndicatorProps) {
  return (
    <div className={cn('flex gap-1', className)}>
      <ProgressBar value={survival} layer="survival" size={size} className="flex-1" />
      <ProgressBar value={maintenance} layer="maintenance" size={size} className="flex-1" />
      <ProgressBar value={growth} layer="growth" size={size} className="flex-1" />
    </div>
  );
}
